import { Alpha3Code } from 'i18n-iso-countries'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, FormField, Select, SelectOption, TextInput } from '@percent/lemonade'
import { getRegistries } from '@percent/workplace-giving/api/registries/registries'
import { RegistriesType } from '@percent/workplace-giving/api/registries/registries.types'
import { volunteeringAllowedOrgCountriesAlpha3 } from '@percent/workplace-giving/common/data/sanctionedCountries'
import { useCountryOptions, useLogger } from '@percent/workplace-giving/common/hooks'
import { useQuery } from '@percent/workplace-giving/common/hooks/useQuery/useQuery'
import classes from './RequestNonprofitWizard.module.scss'
import { RequestNonprofitWizardFormProps } from './RequestNonprofitWizard.types'

export function RequestNonprofitWizardForm({
  values,
  errors,
  handleChange,
  handleBlur,
  setFieldValue,
  touched
}: Readonly<RequestNonprofitWizardFormProps>) {
  const { t } = useTranslation()
  const { logError } = useLogger()
  const countryCode = values.organizationCountry as Alpha3Code

  const { data: registriesData } = useQuery(
    ['getRegistries', { countryCode, supportedTypes: ['nonprofit'] }],
    getRegistries
  )

  const alpha3CountryCodes = useCountryOptions({
    countryCodes: volunteeringAllowedOrgCountriesAlpha3,
    includeWorldOption: false
  })

  const defaultOrgCountryValue = useMemo(
    () => alpha3CountryCodes.find(option => option.value === countryCode) || alpha3CountryCodes[0],
    [alpha3CountryCodes, countryCode]
  )

  const handleCountryChange = useCallback(
    (option: SelectOption) => {
      setFieldValue('organizationCountry', option.value).catch(logError)
      setFieldValue('registryName', '').catch(logError)
    },
    [setFieldValue, logError]
  )

  const registryOptions: SelectOption[] = useMemo(() => {
    if (!registriesData?.data) return []

    return [
      ...registriesData.data.map((registry: RegistriesType) => ({
        value: registry.id,
        label: registry.name
      })),
      {
        value: '',
        label: t('workplace_giving.nonprofit.request.cantFindMyOrganizationRegistry')
      }
    ]
  }, [registriesData, t])

  return (
    <form className={classes.Form} onSubmit={(event: React.FormEvent) => event.preventDefault()}>
      <Alert variant="info">{t('workplace_giving.nonprofit.request.info')}</Alert>

      <FormField
        label={t('workplace_giving.wizard.nonprofitCountry.label')}
        description={t('workplace_giving.wizard.nonprofitCountry.description')}
        status={touched.organizationCountry && errors.organizationCountry ? 'danger' : 'default'}
        statusMessage={errors.organizationCountry}
        data-testid="organizationCountryField"
        necessity="required"
      >
        <Select
          status={errors.organizationCountry ? 'danger' : 'default'}
          placeholder={t('workplace_giving.wizard.nonprofitCountry.placeholder')}
          options={alpha3CountryCodes}
          defaultValue={defaultOrgCountryValue}
          onChange={handleCountryChange}
          searchable
          showPrefixForSelectedOption
          borderRadiiSize="large"
        />
      </FormField>

      <FormField
        label={t('workplace_giving.wizard.organizationName.label')}
        description={t('workplace_giving.wizard.organizationName.description')}
        necessity="required"
        status={touched.organizationName && errors.organizationName ? 'danger' : 'default'}
        statusMessage={errors.organizationName}
        data-testid="organization-name"
      >
        <TextInput
          name="organizationName"
          value={values.organizationName}
          placeholder={t('workplace_giving.wizard.organizationName.placeholder')}
          onBlur={handleBlur}
          onChange={handleChange}
          borderRadiiSize="large"
        />
      </FormField>

      <FormField
        label={t('workplace_giving.wizard.website.label')}
        necessity="required"
        status={touched.organizationWebsite && errors.organizationWebsite ? 'danger' : 'default'}
        statusMessage={errors.organizationWebsite}
        data-testid="organization-website"
      >
        <TextInput
          name="organizationWebsite"
          value={values.organizationWebsite}
          placeholder={t('workplace_giving.wizard.website.placeholder')}
          onBlur={handleBlur}
          onChange={handleChange}
          borderRadiiSize="large"
        />
      </FormField>

      <FormField
        label={t('workplace_giving.wizard.registryId.label')}
        description={t('workplace_giving.wizard.registryId.description')}
        necessity="required"
        optionalText={t('workplace_giving.validation.optional')}
        status={touched.registryId && errors.registryId ? 'danger' : 'default'}
        statusMessage={errors.registryId}
        data-testid="registry-id"
      >
        <TextInput
          name="registryId"
          value={values.registryId}
          placeholder={t('workplace_giving.wizard.registryId.placeholder')}
          onBlur={handleBlur}
          onChange={handleChange}
          borderRadiiSize="large"
        />
      </FormField>

      <FormField
        label={t('workplace_giving.wizard.registry.label')}
        description={t('workplace_giving.wizard.registry.description')}
        necessity="optional"
        optionalText={t('workplace_giving.validation.optional')}
        status={touched.registryName && errors.registryName ? 'danger' : 'default'}
        statusMessage={errors.registryName}
        data-testid="registry-name"
      >
        <Select
          status={errors.registryName ? 'danger' : 'default'}
          placeholder={t('workplace_giving.wizard.registry.placeholder')}
          options={registryOptions}
          onChange={option => {
            setFieldValue('registryName', option.value.length ? option.label : '').catch(logError)
          }}
          showPrefixForSelectedOption
          key={countryCode}
          borderRadiiSize="large"
        />
      </FormField>

      <FormField
        label={t('workplace_giving.wizard.nonprofitEmail.label')}
        description={t('workplace_giving.wizard.nonprofitEmail.description')}
        necessity="optional"
        optionalText={t('workplace_giving.validation.optional')}
        status={touched.email && errors.email ? 'danger' : 'default'}
        statusMessage={errors.email}
        data-testid="email"
      >
        <TextInput
          name="email"
          value={values.email}
          placeholder={t('workplace_giving.wizard.nonprofitEmail.placeholder')}
          type="email"
          onBlur={handleBlur}
          onChange={handleChange}
          borderRadiiSize="large"
        />
      </FormField>
    </form>
  )
}
